import { useEffect, useState } from 'react';

import { AnswerContent } from './answer-content';
import { AnswerHeader } from './answer-header';
import { AnswerSidebar } from './answer-sidebar';
import { FollowUpQuestions } from './follow-up-questions';
import { NoAnswer } from './no-answer';
import { SupportingChunks } from './supporting-chunks';
import { SupportingFacts } from './supporting-facts';
import TooltipModal from '@/components/molecules/Modals/TooltipModal';
import { getConfidenceLevel } from '@/helpers/kimDisplay';
import { cn } from '@/helpers/shadCnUtils';
import { KimMode, MessageType, ThreadMessage } from '@/types';

interface AnswerProps {
  threadId: string;
  caseId: string;
  messages: ThreadMessage[] | undefined;
  isLoadingMessages: boolean;
}

export function Answer({ threadId, caseId, isLoadingMessages, messages }: AnswerProps) {
  const [showAllChunks, setShowAllChunks] = useState(false);
  const [showConfidenceTooltip, setShowConfidenceTooltip] = useState(false);

  // If there are no facts but there are chunks, show the chunks
  useEffect(() => {
    if (!messages) return;
    const answerMessage = messages?.find((message) => message.type === MessageType.Assistant);
    if (!answerMessage) return;
    const { facts, chunks } = answerMessage;
    if (!facts || !chunks) return;
    if (facts.length === 0 && chunks.length > 0) {
      setShowAllChunks(true);
    }
  }, [messages]);

  const userMessage = messages?.find((message) => message.type === MessageType.User);
  const answerMessage = messages?.find((message) => message.type === MessageType.Assistant);
  const confidenceLevel = getConfidenceLevel(answerMessage?.config?.trafficLight);
  const content = answerMessage?.content || '';
  const reasoning = answerMessage?.reasoning || '';
  const facts = answerMessage?.facts || [];
  const chunks = answerMessage?.chunks || [];
  const flowType = answerMessage?.config?.flowType;
  const artifact = answerMessage?.config?.artifacts?.[0];
  const references = answerMessage?.references || {};
  const followUpQuestions = answerMessage?.config?.followUpQuestions || [];
  const messageId = answerMessage?.id || '';

  return (
    <div className="flex h-full">
      <AnswerSidebar userMessage={userMessage} isLoadingMessages={isLoadingMessages} />

      {!isLoadingMessages && (
        <div className="flex h-full w-full flex-1 flex-col">
          <AnswerHeader
            confidenceLevel={confidenceLevel}
            content={content}
            setShowConfidenceTooltip={setShowConfidenceTooltip}
            caseId={caseId}
            threadId={threadId}
            messageId={messageId}
            artifact={artifact}
            flowType={flowType}
            isLoadingMessages={isLoadingMessages}
          />

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {content && content.length > 0 ? (
              <>
                <AnswerContent content={content} reasoning={reasoning} references={references} flowType={flowType} />
                {flowType !== KimMode.ExtractionFlow && flowType !== undefined && (
                  <>
                    <SupportingFacts facts={facts} confidenceLevel={confidenceLevel} references={references} />
                    <SupportingChunks
                      setShowAllChunks={setShowAllChunks}
                      showAllChunks={showAllChunks}
                      chunks={chunks}
                    />
                    <FollowUpQuestions followUpQuestions={followUpQuestions} questionMode={flowType} />
                  </>
                )}
              </>
            ) : (
              <NoAnswer />
            )}
          </div>
        </div>
      )}

      <TooltipModal
        header={
          <div
            className={cn(
              'flex h-full w-full items-center justify-center rounded-t-lg',
              confidenceLevel.backgroundColor,
            )}
          ></div>
        }
        title={confidenceLevel.text}
        content={confidenceLevel.tooltip}
        subtext="All Kim answers have a confidence level, ranging from High to Low."
        isOpen={showConfidenceTooltip}
        handleClose={() => setShowConfidenceTooltip(false)}
      />
    </div>
  );
}
